import { cn } from "@/lib/utils";

/** Fixed height for single-line form controls (text input, select, combobox). */
export const formControlHeightClassName = "h-8 max-h-8 shrink-0";

/** Minimum height when a control grows with content (e.g. multi-select tags). */
export const formControlMinHeightClassName = "min-h-8";

/** Shared padding, typography, and overflow for single-line controls. */
export const formControlSingleLineLayoutClassName =
  "overflow-hidden px-2.5 py-0 text-base leading-8 sm:text-sm";

/** Border and background aligned with Input / Select (white on card in light mode). */
export const formControlSurfaceClassName =
  "border border-input bg-transparent shadow-none transition-colors dark:bg-input/30";

/**
 * Native `:disabled` fill — Input, Textarea, Select trigger.
 * Keep in sync with {@link formControlLockedClassName}.
 */
export const formControlNativeDisabledClassName =
  "disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-input/50 disabled:opacity-50 dark:disabled:bg-input/80";

/**
 * Locked picker shells (Autocomplete, MultiSelect) that are not a native `:disabled` element.
 * Keep in sync with {@link formControlNativeDisabledClassName}.
 */
export const formControlLockedClassName =
  "pointer-events-none cursor-not-allowed bg-input/50 opacity-50 dark:bg-input/80";

/** Single-line picker trigger shell (Autocomplete, empty MultiSelect). */
export const formControlPickerShellClassName = cn(
  formControlHeightClassName,
  formControlSingleLineLayoutClassName,
  "flex items-center gap-1",
);

/** Multi-select trigger when tags are present; grows only when tags wrap. */
export const formControlPickerTagsShellClassName = cn(
  formControlMinHeightClassName,
  "flex h-auto items-start gap-1 overflow-visible px-2.5 py-1.5 text-sm leading-none",
);

/** Native `type="time"` inside popovers — sized to match {@link formControlHeightClassName}. */
export const popoverTimeInputClassName = "popover-time-input tabular-nums";

/** Single-line control inside a floating-label field (56px, text pushed below the label). */
export const floatingControlHeightClassName = "h-14 max-h-14 shrink-0";
export const floatingControlMinHeightClassName = "min-h-14";
export const floatingControlSingleLineLayoutClassName =
  "overflow-hidden px-3 pt-6 pb-1.5 text-base leading-5 sm:text-sm";
/** Multi-line (textarea) inside a floating-label field. */
export const floatingControlMultilineLayoutClassName =
  "min-h-24 px-3 pt-6 pb-2 text-base leading-5 sm:text-sm";
/** Picker trigger shell (Autocomplete, Select, MultiSelect, DatePicker). */
export const floatingControlPickerShellClassName = cn(
  floatingControlHeightClassName,
  floatingControlSingleLineLayoutClassName,
  "flex items-end gap-1",
);
/** MultiSelect trigger when tags are present; grows as tags wrap. */
export const floatingControlPickerTagsShellClassName = cn(
  floatingControlMinHeightClassName,
  "flex h-auto items-end gap-1 overflow-visible px-3 pt-6 pb-1.5 text-sm leading-none",
);

/**
 * Trailing icon / button inside a floating-label control (chevron, clear, calendar).
 *
 * The shell pads asymmetrically (`pt-6 pb-1.5`) so the value clears the floated label,
 * which puts the content box 9px — half the 24px/6px difference — below the center of
 * the 56px border box. `self-center` follows the content box, so shift back up to sit
 * on the true center.
 */
export const floatingControlAdornmentClassName = "self-center -translate-y-[9px]";
